import {
  FlowBlockValueFieldSchema,
  FlowBlockValueFieldSchemaVariant,
  FlowBlockValueTemplateDurationSchema,
  FlowEntityType,
  FlowFormBlockPracticeType,
  FlowFormBlockProviderType,
  FlowFormBlockSchedulingConfigsState,
} from 'flow-schema';
import { z } from 'zod';

import { isDefined, isTruthy } from '../../../utils.js';
import { ExportMode } from '../../previsit/form.js';
import {
  ScheduleAppointmentDisplayBy,
  ScheduleAppointmentSortBy,
} from '../../previsit/formTemplateFieldConfig.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  RunMode,
  StringAsNumberSchema,
} from '../base/baseSchema.js';

/**
 * Shared (unrefined) shape of the form block's config, reused by both the
 * v1 {@link FormBlockSchema} and the v2 `FormV2BlockSchema` (which extends it
 * with a `timeout` for the built-in wait).
 */
export const FormTemplateConfigSchema = z.object({
  formTemplateId: z
    .string({
      error: (issue) => (isDefined(issue.input) ? undefined : 'Form template ID is required'),
    })
    .uuid({ message: 'Form template ID must be a valid UUID' }),
  refreshInterval: FlowBlockValueTemplateDurationSchema().optional(),
});

export const FormBlockBaseConfigShape = z.object({
  formTemplateConfigs: z
    .array(FormTemplateConfigSchema)
    .min(1, 'Form template configuration is required')
    .optional(),
  runMode: z.enum(RunMode).optional().default(RunMode.Live),
  exportMode: z.enum(ExportMode).optional().default(ExportMode.RunAutoExport),
  dynamicParams: z
    .array(
      z
        .object({
          key: z.string().min(1, { message: 'Key cannot be empty' }),
          value: z.string().min(1, { message: 'Value cannot be empty' }),
        })
        .required()
    )
    .refine((dynamicParams) => {
      const keys = dynamicParams.map((param) => param.key);
      return keys.length === new Set(keys).size;
    }, 'The same dynamic parameter key cannot be used twice.')
    .optional(),
  schedulingConfigs: z
    .object({
      schedulingConfigsState: z.enum(FlowFormBlockSchedulingConfigsState),
      fhirAppointmentKinds: z.array(z.string()).optional(),
      practiceType: z.enum(FlowFormBlockPracticeType),
      practiceIds: z.array(z.string()).optional(),
      interpolatedPractice: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
      }).optional(),
      providerType: z.enum(FlowFormBlockProviderType),
      providerIds: z.array(z.string()).optional(),
      fhirDynamicProviders: z.array(z.string()).optional(),
      interpolatedProvider: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
      }).optional(),
      fhirTotalResult: StringAsNumberSchema(z.number().int().min(1).optional()),
      scheduleAppointmentDisplayBy: z.enum(ScheduleAppointmentDisplayBy).optional(),
      scheduleAppointmentSortBy: z.enum(ScheduleAppointmentSortBy).optional(),
      scheduleAppointmentStartTime: FlowBlockValueTemplateDurationSchema().optional(),
      scheduleAppointmentEndTime: FlowBlockValueTemplateDurationSchema().optional(),
      scheduleAppointmentCallSectionEnabled: z.boolean(),
      scheduleAppointmentCommentEnabled: z.boolean(),
      scheduleAppointmentDefaultReasonForVisit: z.string().optional(),
      customConfigs: z
        .object({
          scheduleAppointmentSeriesMinTime: FlowBlockValueTemplateDurationSchema().optional(),
          scheduleAppointmentSeriesMaxTime: FlowBlockValueTemplateDurationSchema().optional(),
          appointmentCommentModalLabel: z.string().optional(),
          appointmentCommentModalPlaceholder: z.string().optional(),
          appointmentCommentModalTitle: z.string().optional(),
          scheduleNextAvailableAppointmentEnabled: z.boolean().optional(),
          useReasonForVisitFromPatientOrders: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
  isOverridingEntity: z.boolean().optional(),
  entityOverride: z
    .object({
      entityType: z.enum(FlowEntityType),
      entityId: z.string(),
    })
    .optional(),
});

export function formBlockEntityOverrideCheck(config: {
  isOverridingEntity?: boolean;
  entityOverride?: { entityType: FlowEntityType; entityId: string };
}): boolean {
  if (!config.isOverridingEntity) {
    return true; // Everything is ok
  }

  // We're overriding the entity, make sure it's specified
  const entity = config.entityOverride;
  if (!isDefined(entity)) {
    return false;
  }

  return isTruthy(entity.entityType) && isTruthy(entity.entityId);
}

export const formBlockEntityOverrideRefineMessage = {
  message: 'When overriding the entity, both entityType and entityId must be specified.',
  path: ['entityOverride', 'entityType'],
};

export const FormBlockConfigSchema = FormBlockBaseConfigShape.refine(
  formBlockEntityOverrideCheck,
  formBlockEntityOverrideRefineMessage
);
export type FormBlockConfig = z.infer<typeof FormBlockConfigSchema>;

export const FormBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Form),
  config: FormBlockConfigSchema,
});
export type FormBlock = z.infer<typeof FormBlockSchema>;
